// AGC: Farm service — Zustand store for active farm selection + TanStack Query for farm data
// AGC: Follows BoviSense pattern: Zustand persist for client state, Query for server data
// AGC: Replaces the internal logic of FarmContext (context wrapper remains for compatibility)
// AGC: Offline-first — caches farm list in localStorage, returns cached data when offline
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, OfflineError } from './api'
import type { Field, Farm } from '../types'

// ============================================================
// AGC: Offline cache — localStorage for farm data
// ============================================================

const FARMS_CACHE_KEY = 'droidfarm_farms_cache'

function cacheFarms(farms: Farm[]): void {
  try {
    localStorage.setItem(FARMS_CACHE_KEY, JSON.stringify({ farms, cachedAt: Date.now() }))
  } catch { /* AGC: ignore storage errors */ }
}

function getCachedFarms(): Farm[] | null {
  try {
    const raw = localStorage.getItem(FARMS_CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return parsed.farms ?? null
    }
  } catch { /* AGC: ignore parse errors */ }
  return null
}

// ============================================================
// AGC: Zustand store — persisted client state (active farm, center coords, wizard)
// ============================================================

interface FarmStore {
  activeFarmId: string | null
  farmCenterCoordinates: [number, number] | null
  needsWizard: boolean
  setActiveFarmId: (id: string | null) => void
  setFarmCenterCoordinates: (coords: [number, number] | null) => void
  setNeedsWizard: (needs: boolean) => void
}

export const useFarmStore = create<FarmStore>()(
  persist(
    (set) => ({
      activeFarmId: null,
      farmCenterCoordinates: null,
      needsWizard: false,
      setActiveFarmId: (id) => set({ activeFarmId: id }),
      setFarmCenterCoordinates: (coords) => set({ farmCenterCoordinates: coords }),
      setNeedsWizard: (needs) => set({ needsWizard: needs }),
    }),
    { name: 'droidfarm-active-farm' },
  ),
)

// ============================================================
// AGC: Query hooks — server state (farm list, CRUD)
// ============================================================

// AGC: Convert backend Field to Farm shape (same type, lots alias)
function fieldToFarm(field: Field): Farm {
  return { ...field, lots: field.plots || [] }
}

/**
 * AGC: Fetch all farms for the authenticated user
 * AGC: Uses centralized api.ts with automatic token injection
 * AGC: Offline-first — returns localStorage cache when offline, updates cache on success
 */
export function useFarmsQuery() {
  return useQuery({
    queryKey: ['farms'],
    queryFn: async (): Promise<Farm[]> => {
      // AGC: If offline, return cached data immediately (skip network)
      if (!navigator.onLine) {
        const cached = getCachedFarms()
        if (cached) return cached
        // AGC: No cache and offline — return empty (FarmContext handles this gracefully)
        throw new OfflineError()
      }

      const data = await api.get<{ fields: Field[] }>('/api/v1/fields')
      const farms = (data.fields || []).map(fieldToFarm)
      // AGC: Cache for offline use
      cacheFarms(farms)
      return farms
    },
    // AGC: Don't retry on auth or offline errors
    retry: (count, error) => {
      if (error instanceof OfflineError) return false
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false
      return count < 2
    },
    staleTime: 30_000,
    // AGC: Seed initial data from cache so the UI renders immediately
    placeholderData: () => getCachedFarms() ?? undefined,
  })
}

/**
 * AGC: Create a new farm
 */
export function useCreateFarmMutation() {
  const qc = useQueryClient()
  const { setActiveFarmId, setNeedsWizard } = useFarmStore()

  return useMutation({
    mutationFn: (data: Partial<Field>) =>
      api.post<Field>('/api/v1/fields', data),
    onSuccess: (field) => {
      const farm = fieldToFarm(field)
      // AGC: Optimistic update — add to cache
      qc.setQueryData<Farm[]>(['farms'], (old) => [...(old || []), farm])
      setActiveFarmId(farm.id)
      setNeedsWizard(false)
    },
  })
}

/**
 * AGC: Update an existing farm (name, location, area, etc.)
 */
export function useUpdateFarmMutation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ farmId, updates }: { farmId: string; updates: Partial<Field> }) =>
      api.put<Field>(`/api/v1/fields/${farmId}`, updates),
    onSuccess: (field) => {
      const farm = fieldToFarm(field)
      // AGC: Optimistic update in cache
      qc.setQueryData<Farm[]>(['farms'], (old) =>
        (old || []).map((f) => (f.id === farm.id ? farm : f)),
      )
    },
  })
}

/**
 * AGC: Delete a farm (only if it has 0 plots)
 */
export function useDeleteFarmMutation() {
  const qc = useQueryClient()
  const { activeFarmId, setActiveFarmId } = useFarmStore()

  return useMutation({
    mutationFn: (farmId: string) =>
      api.delete<void>(`/api/v1/fields/${farmId}`),
    onSuccess: (_, farmId) => {
      // AGC: Remove from cache
      qc.setQueryData<Farm[]>(['farms'], (old) =>
        (old || []).filter((f) => f.id !== farmId),
      )
      // AGC: Clear active farm if it was deleted
      if (activeFarmId === farmId) {
        setActiveFarmId(null)
      }
    },
  })
}
